import { z } from 'zod';

import { ApiError } from '../api/client';
import { TAX_CONDITIONS, ZONES, type TaxCondition, type Zone } from './api';
import { isValidTaxId, normalizeTaxId } from './cuit';

const addressGroupSchema = z.object({
  street: z.string().trim().max(200),
  number: z.string().trim().max(20),
  city: z.string().trim().max(120),
  province: z.string().trim().max(120),
  postalCode: z.string().trim().max(20),
});

/**
 * Mirrors `apps/api` `createCustomerSchema` (`customer.dto.ts`). The empty
 * string default for `taxCondition`/`zone` is intentional: neither has a
 * default value per `customers/spec.md`, so an untouched select must fail
 * `z.enum` validation rather than silently pick the first option.
 */
export const customerFormSchema = z
  .object({
    legalName: z.string().trim().min(1, 'La razón social es obligatoria').max(200),
    tradeName: z.string().trim().min(1, 'El nombre comercial es obligatorio').max(200),
    taxId: z
      .string()
      .trim()
      .min(1, 'El CUIT es obligatorio')
      .transform((raw) => normalizeTaxId(raw))
      .pipe(
        z.string().refine((digits) => isValidTaxId(digits), { message: 'El CUIT no es válido' }),
      ),
    taxCondition: z.enum(TAX_CONDITIONS, { message: 'Seleccioná una condición fiscal' }),
    billingEmail: z
      .string()
      .trim()
      .min(1, 'El email de facturación es obligatorio')
      .email('El email no es válido')
      .max(320),
    billingAddress: addressGroupSchema,
    zone: z.enum(ZONES, { message: 'Seleccioná una zona' }),
    notes: z.string().trim().max(2000),
  })
  .superRefine((data, ctx) => {
    const values = Object.entries(data.billingAddress);
    const anyFilled = values.some(([, value]) => value !== '');

    if (!anyFilled) {
      return;
    }

    for (const [field, value] of values) {
      if (value === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Completá este campo o dejá vacía toda la dirección',
          path: ['billingAddress', field],
        });
      }
    }
  });

/** Validated/transformed shape passed to the submit handler (post-resolver). */
export type CustomerFormValues = z.infer<typeof customerFormSchema>;

export const EMPTY_ADDRESS = { street: '', number: '', city: '', province: '', postalCode: '' };

/**
 * Raw, unvalidated shape bound to the inputs: `taxCondition`/`zone` allow the
 * empty-string "nothing selected" state a real `<select>` starts in, which
 * `CustomerFormValues` (the schema's *output* type) deliberately excludes.
 */
export interface CustomerFormFields {
  legalName: string;
  tradeName: string;
  taxId: string;
  taxCondition: TaxCondition | '';
  billingEmail: string;
  billingAddress: {
    street: string;
    number: string;
    city: string;
    province: string;
    postalCode: string;
  };
  zone: Zone | '';
  notes: string;
}

export const DEFAULT_FORM_VALUES: CustomerFormFields = {
  legalName: '',
  tradeName: '',
  taxId: '',
  taxCondition: '',
  billingEmail: '',
  billingAddress: EMPTY_ADDRESS,
  zone: '',
  notes: '',
};

export type FieldErrorTarget = 'taxId' | 'zone';

/**
 * Server errors never have their `message` parsed: the `code` field on 409/403
 * bodies (`CustomersExceptionFilter`) drives the mapping, per `design.md` §5.
 */
export function mapCreateError(error: unknown): {
  formError?: string;
  fieldError?: { field: FieldErrorTarget; message: string };
} {
  if (error === undefined || error === null) {
    return {};
  }

  if (!(error instanceof ApiError)) {
    return { formError: 'Ocurrió un error de conexión. Intentá de nuevo.' };
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'CUSTOMER_TAX_ID_DUPLICATE') {
    return { fieldError: { field: 'taxId', message: 'Ya existe una empresa activa con ese CUIT' } };
  }

  if (error.status === 403 && code === 'CUSTOMER_ZONE_OUT_OF_SCOPE') {
    return { fieldError: { field: 'zone', message: 'Esa zona está fuera de tu alcance' } };
  }

  if (error.status === 403) {
    return { formError: 'No tenés permiso para realizar esta acción.' };
  }

  if (error.status === 400) {
    return { formError: 'Revisá los datos ingresados e intentá de nuevo.' };
  }

  return { formError: 'Ocurrió un error inesperado. Intentá de nuevo.' };
}
